// src/pages/Marketing/components/CountrySelect.js
//
// Same searchable-combobox mechanics as BusinessTypeSelect.js (see that
// file's own doc comment for why a real <input> stands in for a native
// <select> here — the floating-label collision and the "make it
// searchable" ask apply identically to a ~250-row country list, only more
// so: a plain <select> would be painful to scroll through for this many
// options). Kept as its own small component rather than sharing code with
// BusinessTypeSelect — the two option shapes differ ({slug,label} vs.
// {name,code}) and this list is long enough that de-duplicating the
// filtering/highlight logic isn't worth coupling the two together.
//
// `options` come from DemoRequestForm.js's own content.countries — sourced
// server-side from config.ListCountryOptions() (marketing_content_handler.go),
// the same bundled geodata every tenant's own Country table is seeded from.
//
// `value`/`onChange` operate on the country's NAME, not its ISO code —
// DemoRequest.Country is stored (and server-validated) as the plain name
// (see that model field's own doc comment on why: unlike BusinessType,
// there's no separate translated-label lookup step, so the name already IS
// the one display value both here and in DemoRequests.js).
//
// autoComplete is deliberately NOT the literal string "off" — "country" is
// one of the address fields Chrome's own autofill heuristically recognizes
// from nearby visible label text ("Country"/"الدولة" here), and Chrome
// specifically ignores autocomplete="off" on fields it maps to its address
// autofill, so it would otherwise show its own native suggestion dropdown
// stacked on top of this component's own <ul> list. An unrecognized,
// non-empty token isn't in Chrome's known-token list, so it's actually
// respected. mkt-combobox-arrow is a purely decorative chevron
// (pointer-events: none — clicks pass straight through to the input beneath
// it) standing in for the arrow a native <select> draws automatically, so
// the field still reads as "this opens a list" now that it's a plain text
// input.
import { useEffect, useRef, useState } from "react";

export default function CountrySelect({ value, onChange, options, placeholder, noMatchLabel, id }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);

  const selected = options.find((o) => o.name === value) || null;
  const displayValue = open ? query : selected ? selected.name : "";

  const filtered = query.trim()
    ? options.filter((o) => o.name.toLowerCase().includes(query.trim().toLowerCase()))
    : options;

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  const choose = (opt) => {
    onChange(opt.name);
    setOpen(false);
    setQuery("");
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      e.preventDefault();
      setOpen(true);
      setHighlight(0);
      return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[highlight]) choose(filtered[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    }
  };

  return (
    <div className="mkt-combobox" ref={wrapRef}>
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        autoComplete="country-no-autofill"
        value={displayValue}
        placeholder={open ? placeholder : undefined}
        onFocus={() => {
          setOpen(true);
          setQuery("");
          setHighlight(0);
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onKeyDown={handleKeyDown}
      />
      <span className={`mkt-combobox-arrow${open ? " open" : ""}`} aria-hidden="true">
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      {open && (
        <ul className="mkt-combobox-list" role="listbox">
          {filtered.length === 0 ? (
            <li className="mkt-combobox-empty">{noMatchLabel}</li>
          ) : (
            filtered.map((opt, i) => (
              <li
                key={opt.code}
                role="option"
                aria-selected={opt.name === value}
                className={`mkt-combobox-option${i === highlight ? " highlighted" : ""}${opt.name === value ? " selected" : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(opt);
                }}
                onMouseEnter={() => setHighlight(i)}
              >
                {opt.name}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
